import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AppointmentDetailDrawer,
  type AppointmentSeriesUpdate,
  type AppointmentUpdate,
  CalendarNavigation,
  CalendarWidget,
  EditAppointment,
  EditRecurringEventConfirmationDialog,
  RecurrenceEditMode,
  selectEditingAppointmentId,
  setEditingAppointmentId,
  useAppointments,
  useDeleteAppointmentMutation,
  useDeleteAppointmentSeriesFromAppointmentMutation,
  useUpdateAppointmentMutation,
  useUpdateAppointmentSeriesMutation,
} from '@features/appointments';
import { useFrontChatControl } from '@features/front-chat';
import { BookingRequestsPanelContainer } from '@features/scheduling-settings';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useScheduleReadOnly } from '../hooks/useScheduleReadOnly';
import { useCurrentDateExpiration } from '../store/useCurrentDateExpiration';

import CalendarSidebar from './CalendarSidebar';
import CalendarTemplate from './CalendarTemplate';
import ScheduleListView from './ScheduleListView';

// Inner component that uses AppointmentsContext
export const ScheduleView: React.FC = () => {
  // Check if currentDate has expired on mount and reset if needed
  useCurrentDateExpiration();

  // Navigation and Redux hooks
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const editingAppointmentId = useAppSelector(selectEditingAppointmentId);
  const isReadOnly = useScheduleReadOnly();
  const isCalendarV2Enabled = useFeatureGate(FeatureGates.CALENDAR_V2);
  const { enqueueSnackbar } = useSnackbar();

  // RTK Query mutation hooks
  const [updateAppointmentMutation] = useUpdateAppointmentMutation();
  const [deleteAppointmentMutation] = useDeleteAppointmentMutation();
  const [updateAppointmentSeriesMutation] = useUpdateAppointmentSeriesMutation();
  const [deleteAppointmentSeriesFromAppointmentMutation] =
    useDeleteAppointmentSeriesFromAppointmentMutation();

  // State for recurring event editing
  const [isRecurringEditDialogOpen, setIsRecurringEditDialogOpen] = useState(false);
  const [pendingAppointmentUpdate, setPendingAppointmentUpdate] = useState<{
    appointmentId: string;
    updates: AppointmentUpdate;
    seriesId?: string;
  } | null>(null);

  // State for recurring event deletion
  const [isRecurringDeleteDialogOpen, setIsRecurringDeleteDialogOpen] = useState(false);
  const [pendingAppointmentDelete, setPendingAppointmentDelete] = useState<{
    appointmentId: string;
    seriesId?: string;
  } | null>(null);

  // Get all data from context
  const {
    displayMode,
    refreshAppointments,
    isSidebarOpen,
    setSidebarOpen,
    isRequestsPanelOpen,
    setRequestsPanelOpen,
    setCurrentDate,
  } = useAppointments();

  // Control Front Chat FAB visibility
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { setLauncherVisibility } = useFrontChatControl();

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile, setSidebarOpen, setLauncherVisibility]);
  // Hide FAB when sidebar is closed
  useEffect(() => {
    setLauncherVisibility(isSidebarOpen);
  }, [isSidebarOpen, setLauncherVisibility]);

  // Handle URL params for auto-opening appointment edit sidebar and date navigation
  // Each param is independent: ?edit=APPT_ID, ?date=YYYY-MM-DD, or both
  useEffect(() => {
    const appointmentId = searchParams.get('edit');
    const appointmentDate = searchParams.get('date');

    // Jump to date if provided (independent of edit param)
    if (appointmentDate) {
      const date = new Date(appointmentDate);
      setCurrentDate(date.toISOString());
    }

    // Open edit sidebar if provided (independent of date param) — skip in read-only mode
    if (appointmentId && !isReadOnly) {
      dispatch(setEditingAppointmentId(appointmentId));
    }

    // Clear params after processing to keep URL clean
    if (appointmentId || appointmentDate) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, dispatch, setCurrentDate, setSearchParams, isReadOnly]);

  // Cleanup: Reset editingAppointmentId when component unmounts
  useEffect(() => {
    return () => {
      dispatch(setEditingAppointmentId(null));
    };
  }, [dispatch]);

  const handleOpenAppointmentDetail = (appointmentId: string) => {
    dispatch(setEditingAppointmentId(appointmentId));
  };

  const handleCloseAppointmentDetail = () => {
    dispatch(setEditingAppointmentId(null));
  };

  // Handler for updating appointments, shows dialog for recurring events
  const handleAppointmentUpdate = async (
    appointmentId: string,
    updates: AppointmentUpdate,
    _seriesId?: string,
  ) => {
    // NOTE: Disabling recurring event handling for MVP due to complexity
    // Uncomment the following code to enable recurring event handling

    // If this is a recurring event (has seriesId), show the dialog
    // if (seriesId) {
    //   setPendingAppointmentUpdate({
    //     appointmentId,
    //     updates,
    //     seriesId,
    //   });
    //   setIsRecurringEditDialogOpen(true);
    // } else {
    // Non-recurring event, update directly
    await updateSingleAppointment(appointmentId, updates);
    // }
  };

  // Handler for deleting appointments, shows dialog for recurring events
  const handleAppointmentDelete = async (appointmentId: string, seriesId?: string) => {
    // If this is a recurring event (has seriesId), show the dialog
    if (seriesId) {
      setPendingAppointmentDelete({
        appointmentId,
        seriesId,
      });
      setIsRecurringDeleteDialogOpen(true);
    } else {
      // Non-recurring event, delete directly
      await deleteSingleAppointment(appointmentId);
    }
  };

  // Function to update a single appointment
  const updateSingleAppointment = async (
    appointmentId: string,
    updates: AppointmentUpdate,
  ): Promise<boolean> => {
    try {
      await updateAppointmentMutation({
        appointmentId,
        appointmentUpdate: updates,
      }).unwrap();
      await refreshAppointments();
      dispatch(setEditingAppointmentId(null));
      return true;
    } catch (error) {
      console.error('Error updating appointment:', error);
      return false;
    }
  };

  // Function to update a series
  const updateAppointmentSeries = async (seriesId: string, updates: AppointmentSeriesUpdate) => {
    try {
      await updateAppointmentSeriesMutation({
        seriesId,
        appointmentSeriesUpdate: updates,
      }).unwrap();
      await refreshAppointments();
      dispatch(setEditingAppointmentId(null));
    } catch (error) {
      console.error('Error updating appointment series:', error);
    }
  };

  // Function to delete a single appointment
  const deleteSingleAppointment = async (appointmentId: string) => {
    try {
      await deleteAppointmentMutation({ appointmentId }).unwrap();
      await refreshAppointments();

      // Show success notification
      enqueueSnackbar('Appointment Removed', {
        variant: 'success',
      });

      // Close the edit dialog if it's open
      dispatch(setEditingAppointmentId(null));
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  // Function to delete a series
  const deleteAppointmentSeries = async (seriesId: string, appointmentId: string) => {
    try {
      await deleteAppointmentSeriesFromAppointmentMutation({
        seriesId,
        appointmentId,
      }).unwrap();
      await refreshAppointments();

      // Show success notification
      enqueueSnackbar('Appointment Removed', {
        variant: 'success',
      });

      // Close the edit dialog if it's open
      dispatch(setEditingAppointmentId(null));
    } catch (error) {
      console.error('Error deleting appointment series:', error);
    }
  };

  // Handle recurring edit confirmation
  const handleRecurringEditConfirm = async (mode: RecurrenceEditMode) => {
    if (!pendingAppointmentUpdate) return;

    try {
      if (mode === RecurrenceEditMode.THIS_EVENT) {
        // Update just this event
        await updateSingleAppointment(
          pendingAppointmentUpdate.appointmentId,
          pendingAppointmentUpdate.updates,
        );
      } else if (mode === RecurrenceEditMode.FOLLOWING_EVENTS) {
        // Update this and all following events
        if (pendingAppointmentUpdate.seriesId) {
          await updateAppointmentSeries(
            pendingAppointmentUpdate.seriesId,
            pendingAppointmentUpdate.updates,
          );
        }
      }

      // Clear the pending update
      setPendingAppointmentUpdate(null);

      // Close the EditAppointment dialog as well since the update is complete
      dispatch(setEditingAppointmentId(null));
    } catch (error) {
      console.error('Error handling recurring edit:', error);
    }
  };

  // Handle recurring delete confirmation
  const handleRecurringDeleteConfirm = async (mode: RecurrenceEditMode) => {
    if (!pendingAppointmentDelete) return;

    try {
      if (mode === RecurrenceEditMode.THIS_EVENT) {
        // Delete just this event
        await deleteSingleAppointment(pendingAppointmentDelete.appointmentId);
      } else if (mode === RecurrenceEditMode.FOLLOWING_EVENTS) {
        // Delete this and all following events
        if (pendingAppointmentDelete.seriesId && pendingAppointmentDelete.appointmentId) {
          await deleteAppointmentSeries(
            pendingAppointmentDelete.seriesId,
            pendingAppointmentDelete.appointmentId,
          );
        }
      }

      // Clear the pending delete
      setPendingAppointmentDelete(null);

      // Close the EditAppointment dialog as well since the delete is complete
      dispatch(setEditingAppointmentId(null));
    } catch (error) {
      console.error('Error handling recurring delete:', error);
    }
  };

  return (
    <>
      <CalendarTemplate
        header={<CalendarNavigation />}
        sidebar={<CalendarSidebar />}
        rightPanel={
          isRequestsPanelOpen ? (
            <BookingRequestsPanelContainer
              onClose={() => setRequestsPanelOpen(false)}
              onNavigateToDate={iso => setCurrentDate(new Date(iso).toISOString())}
            />
          ) : undefined
        }
        isSidebarOpen={isSidebarOpen}
        main={
          displayMode === 'list' ? (
            // Show the list view using the standalone component
            <ScheduleListView onAppointmentDelete={handleAppointmentDelete} />
          ) : (
            // Drag wires straight to updateSingleAppointment so it can react to
            // the success flag (resync on a failed lane/time change). The edit
            // pane keeps handleAppointmentUpdate (void); its recurring branch is
            // disabled for MVP, so the two are equivalent here.
            <CalendarWidget
              onAppointmentUpdate={updateSingleAppointment}
              useCalendarV2={isCalendarV2Enabled}
              onAppointmentOpen={handleOpenAppointmentDetail}
            />
          )
        }
      />

      {/* Booking requests on mobile: the right panel is desktop-only, so a
          temporary drawer carries the same container. */}
      {isMobile && (
        <Drawer
          anchor="right"
          open={isRequestsPanelOpen}
          onClose={() => setRequestsPanelOpen(false)}
          slotProps={{ paper: { sx: { width: '100%' } } }}
        >
          <BookingRequestsPanelContainer
            onClose={() => setRequestsPanelOpen(false)}
            onNavigateToDate={iso => setCurrentDate(new Date(iso).toISOString())}
          />
        </Drawer>
      )}

      {/* Edit Recurring Event Confirmation Dialog */}
      <EditRecurringEventConfirmationDialog
        open={isRecurringEditDialogOpen}
        onClose={() => setIsRecurringEditDialogOpen(false)}
        onConfirm={handleRecurringEditConfirm}
        title="Edit Recurring Event"
        confirmButtonText="Save"
        mode="edit"
      />

      {/* Delete Recurring Event Confirmation Dialog */}
      <EditRecurringEventConfirmationDialog
        open={isRecurringDeleteDialogOpen}
        onClose={() => setIsRecurringDeleteDialogOpen(false)}
        onConfirm={handleRecurringDeleteConfirm}
        title="Delete Recurring Event"
        confirmButtonText="Delete"
        mode="delete"
      />

      {/* Calendar v2 detail drawer — hidden in read-only mode */}
      {!isReadOnly && isCalendarV2Enabled && (
        <AppointmentDetailDrawer
          open={!!editingAppointmentId}
          onClose={handleCloseAppointmentDetail}
          appointmentId={editingAppointmentId || undefined}
          onAppointmentDelete={handleAppointmentDelete}
        />
      )}

      {/* Edit Appointment Modal — hidden in read-only mode */}
      {!isReadOnly && !isCalendarV2Enabled && (
        <EditAppointment
          open={!!editingAppointmentId}
          onClose={() => dispatch(setEditingAppointmentId(null))}
          appointmentId={editingAppointmentId || undefined}
          onAppointmentUpdate={handleAppointmentUpdate}
          onAppointmentDelete={handleAppointmentDelete}
        />
      )}

    </>
  );
};

export default ScheduleView;
